import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
} from "@stencil/core";
import { LatLong, Prediction } from "@phn/entities/places";
import { navStateStore } from "@phn/state/nav-state-store";
import { getPPNDealers } from "../../services/dealer/dealer-service";
import { dealerSearchResultsStore } from "../../state/dealer-search-results-store";
import {
  getErrorMessage,
  NaviError,
  NaviErrorTypes,
} from "@phn/utility/navi-error";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import analyticsManager from "../../services/analytics/analytics-manager";
import {
  ClickElementTypes,
  DEALER_SEARCH_DISPLAY_ELEMENT_ID,
  DEALER_SEARCH_RESULT_DISPLAY_ELEMENT_ID_DOUBLE_DRAWER,
  DEALER_SEARCH_TARGET_ELEMENT,
  ElementTypes,
  EventActions,
  GEOLOCATION_BUTTON_SEARCH_TERM,
} from "@phn/utility/constants";
import { RoutingKeys } from "@phn/entities/routing-key";
import mapsService from "../../services/maps-service";
import { Timeout } from "@phn/utility/timeout";
import { navContentStore } from "@phn/state/nav-content-store";
import { componentsOnReady } from "@phn/utility/ready";
import { getConfig, getString, isString } from "@phn/utility/helper";
import logger from "@phn/services/logger";
import {
  getRegionSpecificCountry,
  getUsedUnit,
} from "../../services/dealer/dealer-search-helper";
import { splitLocale } from "@phn/entities/locale";
import windowService from "@phn/services/window-service";
import ErrorMessage from "../dealer-search-form/error-message";
import { Components } from "../../components";
import PhnNdDealerSearchFormRadius = Components.PhnNdDealerSearchFormRadius;

@Component({
  tag: "phn-nd-dealer-search-form-radius",
  styleUrl: "dealer-search-form-radius.scss",
  scoped: true,
})
export class DealerSearchFormRadius {
  @Prop() locale!: string;

  @State() predictions: Prediction[] =
    dealerSearchResultsStore.get("predictions");

  @State() errorMessage: string | null = null;

  @State() formErrorMessage: string | null = null;

  @State() animateResults = false;

  @State() searchTerm = dealerSearchResultsStore.get("searchTerm");

  @State() showSkeleton = !navStateStore.state.dealerSeachLoaded;

  @State() usedLocation = false;

  @State() geolocating = false;

  @Event() dealerSearchFormEvent!: EventEmitter;

  @Event() clearSearchEvent!: EventEmitter;

  @Event() loadingResults!: EventEmitter<boolean>;

  @Event() removeSkeletonOnDealerSearchForm!: EventEmitter;

  @Element() el!: HTMLElement;

  private debounceTimeout = new Timeout(100);

  private radiusOptions = ["50", "100", "200"];

  private radiusUnit = getUsedUnit(splitLocale(this.locale).country);

  private async waitForComponentsToLoad() {
    await componentsOnReady(this.el, new Timeout(5000));
    this.removeSkeletonOnDealerSearchForm.emit();
  }

  disconnectedCallback(): void {
    if (this.usedLocation) {
      dealerSearchResultsStore.set("predictions", []);
    }
    this.debounceTimeout.cancel();
  }

  componentWillLoad(): void {
    if (navStateStore.state.dealerSeachLoaded) {
      this.removeSkeletonOnDealerSearchForm.emit();
    }

    this.waitForComponentsToLoad().then(() => {
      const { error: errorContent } = navContentStore.state;
      this.showSkeleton = false;

      mapsService.init("GOOGLE_API_KEY").catch((err: Error) => {
        logger.error("INIT_MAPS_SERVICE", err);
        this.formErrorMessage = getErrorMessage(errorContent, err);
        this.errorMessage = null;
      });
    });
  }

  componentDidRender() {
    navStateStore.state.dealerSeachLoaded = true;
  }

  componentDidLoad(): void {
    this.emitPageLoadEvent();
  }

  private emitPageLoadEvent() {
    const { dealer } = navContentStore.state;
    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.DEALER_SEARCH_SCREEN_LOAD,
      {
        componentDisplay: {
          displayElementId: analyticsManager.calculateDisplayElementId(),
          displayElementType: analyticsManager.calculateDisplayElementType(
            "",
            ElementTypes.dealerSearchForm,
            true,
          ),
          displayElementName: dealer.text,
        },
      },
    );
  }

  private emitResultsLoadEvent(searchTerm: string) {
    const { results } = dealerSearchResultsStore.state;
    const { dealer: dealerContent } = navContentStore.state;

    const componentSearchSearchTerm = isString(searchTerm)
      ? searchTerm
      : GEOLOCATION_BUTTON_SEARCH_TERM;
    const numberOfResults = results?.length || 0;

    GlobalDataLayerService.getInstance().pushDataLayerEvent(
      EventActions.DEALER_RESULTS_SCREEN_LOAD,
      {
        componentDisplay: {
          displayElementId:
            analyticsManager.getDealerResultsLoadDisplayElementId(
              DEALER_SEARCH_RESULT_DISPLAY_ELEMENT_ID_DOUBLE_DRAWER,
            ),
          displayElementType:
            analyticsManager.getDealerResultsLoadDisplayElementType(),
          displayElementName: dealerContent.text,
        },
        componentSearch: {
          searchTerm: componentSearchSearchTerm,
          numberOfResults,
        },
      },
    );
  }

  private async fetchDealers(
    getSearchTerms: () => Promise<[string, LatLong, string]>,
  ) {
    const { error: errorContent } = navContentStore.state;
    try {
      this.reset();
      this.loadingResults.emit(true);

      const [searchTerm, placeCoordinates, radius] = await getSearchTerms();
      const { env } = navStateStore.state;
      dealerSearchResultsStore.set("placeCoordinates", placeCoordinates);

      const ppnDealerSearchResults = await getPPNDealers(
        env,
        getConfig(env).BFF_ENDPOINT,
        placeCoordinates,
        getRegionSpecificCountry(splitLocale(this.locale).country),
        { radius: Number(radius) },
      );

      dealerSearchResultsStore.set("searchTerm", searchTerm);
      dealerSearchResultsStore.set(
        "results",
        ppnDealerSearchResults.map(({ ppnDealer: dealer }) => dealer.id),
      );
      dealerSearchResultsStore.set(
        "ppnDealers",
        ppnDealerSearchResults.reduce((dealers, dealerSearchResult) => {
          return {
            ...dealers,
            [dealerSearchResult.ppnDealer.id]: dealerSearchResult,
          };
        }, dealerSearchResultsStore.get("ppnDealers")),
      );
    } catch (err) {
      logger.error("FETCH_DEALERS", err as Error);
      this.formErrorMessage = getErrorMessage(errorContent, err as Error);
      this.errorMessage = null;
    } finally {
      this.loadingResults.emit(false);
      this.animateResults = true;
    }
  }

  private async onSubmit(prediction: Prediction | null) {
    const { dealer: dealerContent, error: errorContent } =
      navContentStore.state;

    if (!isString(this.searchTerm)) {
      this.errorMessage = errorContent.noInput;
      this.formErrorMessage = "";
      return;
    }

    await this.fetchDealers(async () => {
      if (prediction === null) {
        throw new NaviError(
          "Dealer search form submitted without prediction",
          NaviErrorTypes.INVALID_INPUT,
        );
      }

      this.searchTerm = prediction.text;

      GlobalDataLayerService.getInstance().pushDataLayerEvent(
        EventActions.DEALER_SEARCH_CLICK,
        {
          componentClick: {
            clickElementId: prediction.id,
            clickElementName: prediction.text,
            clickElementType: ClickElementTypes.Button,
            targetElement: DEALER_SEARCH_TARGET_ELEMENT,
          },
          componentDisplay: {
            displayElementId: DEALER_SEARCH_DISPLAY_ELEMENT_ID,
            displayElementType: analyticsManager.calculateDisplayElementType(
              RoutingKeys.DEALER_SEARCH,
              ElementTypes.dealerSearchForm,
            ),
            displayElementName: dealerContent.text,
          },
          componentSearch: {
            searchTerm: prediction.text,
          },
        },
      );
      try {
        const place = new google.maps.places.Place({
          id: prediction.id,
        });
        mapsService.place = place;
      } catch {
        // eslint-disable-next-line no-console
        console.error("Failed to create place object");
      }
      const placeCoordinates = await mapsService.getPlaceCoordinates();

      return [
        prediction.text,
        placeCoordinates,
        dealerSearchResultsStore.get("radius"),
      ];
    });

    this.emitResultsLoadEvent(this.searchTerm);
  }

  private async getCurrentLocation(): Promise<void> {
    const { dealer: dealerContent, error: errorContent } =
      navContentStore.state;
    let placeCoordinates: LatLong | null = null;
    this.geolocating = true;
    try {
      placeCoordinates = await windowService.getNavigatorLatLong();
    } catch (error) {
      this.errorMessage = getErrorMessage(errorContent, error as Error);
      return;
    } finally {
      this.geolocating = false;
    }

    await this.fetchDealers(async () => {
      const formattedAddress =
        await mapsService.getFormattedAddressFromCoordinates(placeCoordinates);

      this.searchTerm = "";
      this.usedLocation = true;

      GlobalDataLayerService.getInstance().pushDataLayerEvent(
        EventActions.DEALER_GEOLOCATION_CLICK,
        {
          componentClick: {
            clickElementId: dealerContent.search.geolocation.id,
            clickElementName: dealerContent.search.geolocation.text,
            clickElementType: ClickElementTypes.Button,
            targetElement: DEALER_SEARCH_TARGET_ELEMENT,
          },
          componentDisplay: {
            displayElementId: DEALER_SEARCH_DISPLAY_ELEMENT_ID,
            displayElementType: analyticsManager.calculateDisplayElementType(
              RoutingKeys.DEALER_SEARCH,
              ElementTypes.dealerSearchForm,
            ),
            displayElementName: dealerContent.text,
          },
          componentSearch: {
            searchTerm: GEOLOCATION_BUTTON_SEARCH_TERM,
          },
        },
      );

      return [
        formattedAddress,
        placeCoordinates,
        dealerSearchResultsStore.get("radius"),
      ];
    });

    this.emitResultsLoadEvent(GEOLOCATION_BUTTON_SEARCH_TERM);
  }

  private async onDealerSearchEntry(searchTerm: string): Promise<void> {
    const { error } = navContentStore.state;
    try {
      this.searchTerm = searchTerm;
      this.errorMessage = null;
      this.formErrorMessage = null;
      this.predictions = await mapsService.getPredictions(
        searchTerm,
        this.locale,
      );
    } catch (err) {
      logger.error("GET_PREDICTIONS", err as Error);
      this.errorMessage = getErrorMessage(error, err as Error);
      this.predictions = [];
    }
  }

  private updateRadius(radius: string) {
    dealerSearchResultsStore.set("radius", radius);
    const prediction = this.predictions.find((p) => p.text === this.searchTerm);
    if (prediction) {
      this.onSubmit(prediction);
    } else if (this.usedLocation) {
      this.getCurrentLocation();
    }
  }

  private reset() {
    this.errorMessage = null;
    this.formErrorMessage = null;
    this.animateResults = false;
    this.usedLocation = false;
    this.loadingResults.emit(false);

    dealerSearchResultsStore.set("results", null);
    dealerSearchResultsStore.set("placeCoordinates", null);
  }

  render(): PhnNdDealerSearchFormRadius {
    const { dealer: dealerContent } = navContentStore.state;

    return (
      <Host class={this.showSkeleton ? "skeleton" : ""}>
        <form
          class={"form-container"}
          onSubmit={(e) => {
            e.preventDefault();
            const { error: errorContent } = navContentStore.state;
            if (!isString(this.searchTerm) && !this.usedLocation) {
              this.errorMessage = errorContent.noInput;
              this.formErrorMessage = "";
              return;
            }
          }}
        >
          <div class="input-container">
            {this.showSkeleton ? (
              <div class="skeleton-autocomplete-search-field" />
            ) : null}
            <phn-nd-autocomplete-search-geolocation-field
              placeholder={dealerContent.search.form.inputPlaceholder}
              initialValue={dealerSearchResultsStore.get("searchTerm")}
              predictions={this.predictions}
              onSearchInput={(e) =>
                this.debounceTimeout.set(async () =>
                  this.onDealerSearchEntry(e.detail),
                )
              }
              onClearSearch={() => {
                this.predictions = [];
                dealerSearchResultsStore.set("searchTerm", "");
                dealerSearchResultsStore.set("predictions", []);
                this.searchTerm = "";
                this.reset();
                this.clearSearchEvent.emit();
              }}
              isGeolocating={this.geolocating}
              onGeolocationClick={async () => this.getCurrentLocation()}
              onSubmitSearch={async (e) => this.onSubmit(e.detail)}
              errorMessage={getString(this.errorMessage)}
              style={{ display: this.showSkeleton ? "none" : "" }}
            />
          </div>

          <div class="input-container">
            {this.showSkeleton ? (
              <div class="skeleton-autocomplete-search-field" />
            ) : null}
            <phn-p-select
              class="radius"
              name="radius"
              label={dealerContent.search.form.radiusLabel ?? "Radius"}
              data-testid="radius"
              value={dealerSearchResultsStore.get("radius")}
              style={{
                display: this.showSkeleton ? "none" : "",
                "--p-select-background-color": "transparent",
              }}
              disabled={this.geolocating}
              onUpdate={(e) => {
                this.updateRadius(e.detail.value);
              }}
            >
              {this.radiusOptions.map((option) => (
                <phn-p-select-option key={option} value={option}>
                  {`${option} ${this.radiusUnit}`}
                </phn-p-select-option>
              ))}
            </phn-p-select>
          </div>

          {isString(this.formErrorMessage) ? (
            <ErrorMessage
              errorMessage={this.formErrorMessage}
              onDismiss={() => this.reset()}
            />
          ) : null}
        </form>
      </Host>
    );
  }
}
